import {
  AccountCircle,
  ChevronLeft,
  ChevronRight,
  Leaderboard,
  Login,
  NoteAlt,
  RateReview,
  RequestPage,
  Search,
} from '@mui/icons-material';
import {
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { styled as MuiStyled } from '@mui/material/styles';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import kandirLogo from '../../assets/images/kandir_logo.png';
import { UserContext } from '../contexts/UserContext';
import { useResponsive } from '@shared/hooks/useResponsive';
import { layout } from '@shared/theme/utils';

interface SideDrawerProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const drawerWidthExpanded = 280;
const drawerWidthCollapsed = 72;

// Routes where the side drawer should be completely hidden
const HIDDEN_DRAWER_ROUTES = [
  '/',
  '/sign-in',
  '/sign-up',
  '/verification',
  '/onboarding',
];

export function shouldHideDrawer(pathname: string): boolean {
  return HIDDEN_DRAWER_ROUTES.some(
    (route) =>
      pathname === route ||
      pathname.startsWith(`${route}/`) ||
      pathname.startsWith(`${route}?`)
  );
}

export const Main = MuiStyled('main', {
  shouldForwardProp: (prop) =>
    prop !== 'open' && prop !== 'isMobile' && prop !== 'hideDrawer',
})<{
  open?: boolean;
  isMobile?: boolean;
  hideDrawer?: boolean;
}>(({ theme, open, isMobile, hideDrawer }) => ({
  flexGrow: 1,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  '--sidebar-width': hideDrawer
    ? '0px'
    : !isMobile
      ? open
        ? `${drawerWidthExpanded}px`
        : `${drawerWidthCollapsed}px`
      : open
        ? `${drawerWidthExpanded}px`
        : '0px',
  marginLeft: hideDrawer
    ? '0px'
    : !isMobile
      ? open
        ? `${drawerWidthExpanded}px`
        : `${drawerWidthCollapsed}px`
      : '0px',
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: ${layout.navbarHeight}px;
  min-height: ${layout.navbarHeight}px;
  padding: 0 16px;
  background: linear-gradient(90deg, #c05a3a 0%, #d4825e 100%);
`;

const CollapsedDrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${layout.navbarHeight}px;
  padding: 0;
  background: linear-gradient(90deg, #c05a3a 0%, #d4825e 100%);
`;

const StyledImage = styled.img`
  height: 50px;
  cursor: pointer;
`;

const StyledDrawer = styled(Drawer)<{ $isCollapsed?: boolean }>`
  .MuiDrawer-paper {
    width: ${({ $isCollapsed }) =>
      $isCollapsed ? drawerWidthCollapsed : drawerWidthExpanded}px;
    box-sizing: border-box;
    overflow-x: hidden;
    transition: width 0.3s ease;
  }
`;

const StyledListItemIcon = styled(ListItemIcon)<{ $isCollapsed?: boolean }>`
  min-width: 40px;
  justify-content: ${({ $isCollapsed }) =>
    $isCollapsed ? 'center' : 'flex-start'};
  min-width: ${({ $isCollapsed }) => ($isCollapsed ? 'auto' : '40px')};
`;

const StyledListItemButton = styled(ListItemButton)<{ $isActive?: boolean }>`
  &:hover {
    background-color: rgba(255, 255, 255, 0.15);
    transform: translateX(4px);
    transition: all 0.3s ease;
  }

  ${({ $isActive }) =>
    $isActive &&
    `
    background-color: rgba(192, 90, 58, 0.15);
    border-right: 3px solid #c05a3a;
    
    .MuiListItemIcon-root {
      color: #c05a3a;
    }
    
    .MuiListItemText-primary {
      font-weight: 600;
      color: #c05a3a;
    }
  `}
`;

const StyledListItemText = styled(ListItemText)`
  span {
    font-size: 15px;
    letter-spacing: 0.5px;
  }
`;

const SectionLabel = styled.div`
  padding: 16px 16px 8px 16px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: rgba(0, 0, 0, 0.6);
`;

function SideDrawer({ isOpen, setIsOpen }: SideDrawerProps) {
  const { user } = useContext(UserContext);
  const { isMobile } = useResponsive();
  const location = useLocation();
  const hasFired = useRef(false);

  useEffect(() => {
    if (!hasFired.current) {
      hasFired.current = true;
    }
  }, []);

  const isActive = (path: string) => {
    return (
      location.pathname === path ||
      (path === '/contribute' && location.pathname.startsWith('/contribute')) ||
      (path === '/my-reviews' && location.pathname.startsWith('/my-reviews'))
    );
  };

  const CONTRIBUTE_MAPPINGS = useMemo(
    () => [
      {
        text: 'Contributor Dashboard',
        icon: <NoteAlt />,
        path: '/my-reviews',
      },
      {
        text: 'Company Search',
        icon: <Search />,
        path: '/company-search',
      },
      {
        text: 'Open Intelligence Requests',
        icon: <RequestPage />,
        path: '/intelligence-requests',
      },
    ],
    []
  );

  const ADMIN_MAPPINGS = useMemo(
    () => [
      {
        text: 'Admin View',
        icon: <Leaderboard />,
        path: '/admin',
      },
      {
        text: 'Admin Review Feed',
        icon: <RateReview />,
        path: '/admin/reviews',
      },
    ],
    []
  );

  const LOGIN_MAPPINGS = useMemo(
    () => [
      {
        text: 'Login',
        icon: <Login />,
        path: '/sign-in',
      },
    ],
    []
  );

  const isCollapsed = !isOpen && !isMobile;

  // Hide drawer completely on auth-related routes
  // This must come AFTER all hooks to comply with Rules of Hooks
  if (shouldHideDrawer(location.pathname)) {
    return null;
  }

  return (
    <StyledDrawer
      variant={isMobile ? 'temporary' : 'persistent'}
      anchor="left"
      open={isMobile ? isOpen : true}
      onClose={() => setIsOpen(false)}
      $isCollapsed={isCollapsed}
    >
      {isCollapsed ? (
        <CollapsedDrawerHeader>
          <IconButton onClick={() => setIsOpen(true)} size="small">
            <ChevronRight sx={{ color: 'white' }} />
          </IconButton>
        </CollapsedDrawerHeader>
      ) : (
        <DrawerHeader>
          <Link
            to="/my-reviews"
            style={{ textDecoration: 'none', display: 'flex' }}
          >
            <StyledImage src={kandirLogo} />
          </Link>
          {isMobile && (
            <IconButton onClick={() => setIsOpen(false)}>
              <ChevronLeft />
            </IconButton>
          )}
          {!isMobile && (
            <IconButton onClick={() => setIsOpen(false)}>
              <ChevronLeft sx={{ color: 'white' }} />
            </IconButton>
          )}
        </DrawerHeader>
      )}

      <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.2)' }} />

      {/* Unauthenticated - Company Search */}
      {(!user || !user?.idToken) && (
        <>
          <ListItem disablePadding>
            <Tooltip
              title={isCollapsed ? 'Company Search' : ''}
              placement="right"
              arrow
            >
              <Link
                to="/company-search"
                style={{
                  textDecoration: 'none',
                  color: 'inherit',
                  width: '100%',
                }}
              >
                <StyledListItemButton
                  $isActive={isActive('/company-search')}
                  data-testid="side-nav-company-search"
                  sx={{ justifyContent: isCollapsed ? 'center' : 'flex-start' }}
                >
                  <StyledListItemIcon $isCollapsed={isCollapsed}>
                    <Search />
                  </StyledListItemIcon>
                  {!isCollapsed && (
                    <StyledListItemText primary="Company Search" />
                  )}
                </StyledListItemButton>
              </Link>
            </Tooltip>
          </ListItem>
          <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.2)' }} />
        </>
      )}

      {/* CONTRIBUTE section - authenticated users */}
      {user && user.idToken && (
        <>
          <List sx={{ py: 0 }}>
            {CONTRIBUTE_MAPPINGS.map(({ text, icon, path }, idx) => (
              <ListItem key={idx} disablePadding>
                <Tooltip
                  title={isCollapsed ? text : ''}
                  placement="right"
                  arrow
                >
                  <Link
                    to={path}
                    style={{
                      textDecoration: 'none',
                      color: 'inherit',
                      width: '100%',
                    }}
                  >
                    <StyledListItemButton
                      $isActive={isActive(path)}
                      data-testid={`side-nav${path.toLowerCase().replace(/\//g, '-')}`}
                      sx={{
                        justifyContent: isCollapsed ? 'center' : 'flex-start',
                      }}
                    >
                      <StyledListItemIcon $isCollapsed={isCollapsed}>
                        {icon}
                      </StyledListItemIcon>
                      {!isCollapsed && <StyledListItemText primary={text} />}
                    </StyledListItemButton>
                  </Link>
                </Tooltip>
              </ListItem>
            ))}
            {user?.username && (
              <ListItem disablePadding>
                <Tooltip
                  title={isCollapsed ? 'My Profile' : ''}
                  placement="right"
                  arrow
                >
                  <Link
                    to={`/user/${user.username}`}
                    style={{
                      textDecoration: 'none',
                      color: 'inherit',
                      width: '100%',
                    }}
                  >
                    <StyledListItemButton
                      $isActive={location.pathname === `/user/${user.username}`}
                      data-testid="side-nav-my-profile"
                      sx={{
                        justifyContent: isCollapsed ? 'center' : 'flex-start',
                      }}
                    >
                      <StyledListItemIcon $isCollapsed={isCollapsed}>
                        <AccountCircle />
                      </StyledListItemIcon>
                      {!isCollapsed && <StyledListItemText primary="My Profile" />}
                    </StyledListItemButton>
                  </Link>
                </Tooltip>
              </ListItem>
            )}
          </List>
          <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.2)' }} />
        </>
      )}

      {/* ADMIN section - admins only */}
      {user?.isAdmin && (
        <>
          {!isCollapsed && <SectionLabel>Admin</SectionLabel>}
          <List sx={{ py: 0 }}>
            {ADMIN_MAPPINGS.map(({ text, icon, path }, idx) => (
              <ListItem key={idx} disablePadding>
                <Tooltip
                  title={isCollapsed ? text : ''}
                  placement="right"
                  arrow
                >
                  <Link
                    to={path}
                    style={{
                      textDecoration: 'none',
                      color: 'inherit',
                      width: '100%',
                    }}
                  >
                    <StyledListItemButton
                      $isActive={isActive(path)}
                      data-testid={`side-nav${path.toLowerCase().replace(/\//g, '-')}`}
                      sx={{
                        justifyContent: isCollapsed ? 'center' : 'flex-start',
                      }}
                    >
                      <StyledListItemIcon $isCollapsed={isCollapsed}>
                        {icon}
                      </StyledListItemIcon>
                      {!isCollapsed && <StyledListItemText primary={text} />}
                    </StyledListItemButton>
                  </Link>
                </Tooltip>
              </ListItem>
            ))}
          </List>
          <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.2)' }} />
        </>
      )}

      {/* Login - unauthenticated users */}
      {!user && (
        <List sx={{ py: 0 }}>
          {LOGIN_MAPPINGS.map(({ text, icon, path }, idx) => (
            <ListItem key={idx} disablePadding>
              <Tooltip title={isCollapsed ? text : ''} placement="right" arrow>
                <Link
                  to={path}
                  style={{
                    textDecoration: 'none',
                    color: 'inherit',
                    width: '100%',
                  }}
                >
                  <StyledListItemButton
                    $isActive={isActive(path)}
                    data-testid={`side-nav${path.toLowerCase().replace(/\//g, '-')}`}
                    sx={{
                      justifyContent: isCollapsed ? 'center' : 'flex-start',
                    }}
                  >
                    <StyledListItemIcon $isCollapsed={isCollapsed}>
                      {icon}
                    </StyledListItemIcon>
                    {!isCollapsed && <StyledListItemText primary={text} />}
                  </StyledListItemButton>
                </Link>
              </Tooltip>
            </ListItem>
          ))}
        </List>
      )}
    </StyledDrawer>
  );
}

export default SideDrawer;
